import axios, { AxiosError, AxiosInstance } from "axios";
import axiosRetry from "axios-retry";
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateOAuthState,
  setPkceState,
  getPkceState,
  clearPkceState,
} from "./common.util";
import {
  WebAuthOptions,
  IWebAuth,
  LoginOptions,
  LoginCallback,
  PasswordlessStartOptions,
  PasswordlessStartCallback,
  PasswordlessLoginOptions,
  PasswordlessLoginCallback,
  CheckSessionCallback,
  ParseHashOptions,
  ParseHashCallback,
  LogoutOptions,
  Auth0Error,
  Auth0DecodedHash,
  AuthPasswordlessStartResponse,
  AuthLogoutResponse,
} from "../types/auth.types";

// OAuth 2.1準拠のトークン管理インターフェース
interface TokenData {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType?: string;
  scope?: string;
}

interface TokenStorage {
  saveTokens(tokens: TokenData): void;
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  clearTokens(): void;
  getClientInfo(): string | null;
}

/**
 * DM新認証基盤用Auth0互換WebAuthクラス
 * auth0-jsライブラリのWebAuthクラスと完全に互換性を持つ
 */
export class WebAuth implements IWebAuth {
  private domain: string;
  private clientID: string;
  private scope?: string;
  private responseType?: string;
  private redirectUri?: string;
  private httpClient: AxiosInstance;
  private tokenStorage?: TokenStorage;

  constructor(options: WebAuthOptions, tokenStorage?: TokenStorage) {
    this.domain = options.domain;
    this.clientID = options.clientID;
    this.scope = options.scope;
    this.responseType = options.responseType;
    this.redirectUri = options.redirectUri;
    this.tokenStorage = tokenStorage;

    // 新認証基盤用HTTPクライアントの初期化
    this.httpClient = axios.create({
      baseURL: this.domain,
      timeout: 5000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // axios-retryの設定: ネットワークエラーとタイムアウトのみリトライ
    // タイムアウト: 初回5秒 → 1回目リトライ10秒 → 2回目リトライ30秒
    axiosRetry(this.httpClient, {
      retries: 2,
      retryDelay: (retryCount) => retryCount * 1000,
      retryCondition: (error: AxiosError) => {
        // ネットワークエラーまたはタイムアウトのみリトライ対象
        // 5xxエラーは認証サーバー到達済みなのでリトライしない
        return (
          axiosRetry.isNetworkError(error) ||
          (axiosRetry.isRetryableError(error) && error.code === "ECONNABORTED")
        );
      },
      onRetry: (retryCount, _error, requestConfig) => {
        // リトライごとにタイムアウトを延長
        // retryCount=1 → 10秒, retryCount=2 → 30秒
        if (retryCount === 1) {
          requestConfig.timeout = 10000;
        } else if (retryCount === 2) {
          requestConfig.timeout = 30000;
        }
      },
    });

    // リクエストインターセプターでtabSessionIdを追加
    this.httpClient.interceptors.request.use((config) => {
      const clientInfo = this.tokenStorage?.getClientInfo();
      if (clientInfo) {
        config.headers["X-Dmid-Trace-info"] = clientInfo;
      }
      return config;
    });

    // レスポンスインターセプターでエラーハンドリング
    this.httpClient.interceptors.response.use(
      (response) => response,
      (error) => {
        // Auth0互換のエラー形式に変換
        const auth0Error: Auth0Error = this.convertHttpErrorToAuth0Error(error);
        return Promise.reject(auth0Error);
      }
    );
  }

  /**
   * パスワード認証 - OAuth 2.1 Authorization Code Flow + PKCE
   * dm-id-server /authorize エンドポイント対応
   * fetch + redirect:followを使用してエラーハンドリングを適切に行う
   */
  async login(options: LoginOptions, callback: LoginCallback): Promise<void> {
    // パラメータバリデーション
    if (!options.username || !options.password) {
      const validationError: Auth0Error = {
        error: "invalid_request",
        errorDescription: "Username and password are required",
      };
      callback(validationError);
      return;
    }

    try {
      // PKCE & state 生成
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateOAuthState();

      // PKCE状態をCookieに保存（5分間）
      setPkceState(codeVerifier, state);

      // OAuth 2.1 Authorization Code Flow用のリクエストデータを構築
      const authorizeRequest = {
        response_type: "code", // 'token' から 'code' に変更
        client_id: this.clientID,
        redirect_uri: options.redirectUri || this.redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        scope: options.scope || this.scope || "openid profile email",
        state: state,
        credentials: {
          username: options.username,
          password: options.password,
        },
        // realm: options.realm, // dm-id-serverではcredentials内で処理
      };

      // dm-id-serverの /authorize エンドポイントに送信（POST form形式）
      // fetch + redirect:followを使用してエラーハンドリングを適切に行う
      // 成功時は自動リダイレクト、エラー時はJavaScriptでハンドリング

      // デバッグ用: client_idの確認
      console.log("DM_ID_CLIENT_ID:", this.clientID);
      if (!this.clientID) {
        const auth0Error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Client ID is not configured. Please set REACT_APP_DM_ID_CLIENT_ID environment variable.",
        };
        callback(auth0Error);
        return;
      }

      try {
        // URLSearchParamsを使用してx-www-form-urlencoded形式のデータを構築
        const formData = new URLSearchParams();
        Object.entries(authorizeRequest).forEach(([key, value]) => {
          formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
        });

        const response = await this.httpClient.post("/authorize", formData, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        // 200 OK + JSONレスポンスの場合（Auth0方式）
        if (response.status === 200 && response.data) {
          // 成功時: redirect_urlでリダイレクト
          if (response.data.status === "success" && response.data.redirect_uri) {
            console.log("[TRACE] Success - Redirecting to:", response.data.redirect_uri);

            try {
              // ブラウザを手動でリダイレクト
              window.location.href = response.data.redirect_uri;
              console.log("[TRACE] window.location.href executed successfully");
            } catch (redirectError) {
              console.error("[ERROR] window.location.href failed:", redirectError);
              const auth0Error: Auth0Error = {
                error: "server_error",
                errorDescription: "Failed to redirect browser",
                original: redirectError,
              };
              callback(auth0Error);
            }
            return;
          }

          // エラー時: エラーハンドリング
          if (response.data.status === "error") {
            const auth0Error: Auth0Error = {
              error: response.data.error || "access_denied",
              errorDescription: response.data.error_description || "Authentication failed",
              statusCode: response.status,
            };
            console.log("[TRACE] 認証失敗");
            callback(auth0Error);
            return;
          }

          // 予期しないレスポンス形式
          const auth0Error: Auth0Error = {
            error: "server_error",
            errorDescription: "Unexpected response format from server",
            statusCode: response.status,
          };
          callback(auth0Error);
          return;
        }
      } catch (axiosError) {
        const auth0Error: Auth0Error = {
          error: "server_error",
          errorDescription: "Network error during authorization",
          original: axiosError,
        };
        callback(auth0Error);
      }
    } catch (error: unknown) {
      // PKCE生成エラー
      const pkceError: Auth0Error = {
        error: "server_error",
        errorDescription: "Failed to generate PKCE parameters",
        original: error,
      };
      callback(pkceError);
    }
  }

  /**
   * OTP送信（パスワードレス認証開始）
   * auth0.webAuth.passwordlessStart()と完全互換
   */
  passwordlessStart(options: PasswordlessStartOptions, callback: PasswordlessStartCallback): void {
    // パラメータバリデーション - Auth0と同様のエラーハンドリング
    if (!options.connection) {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: "Connection type is required",
      };
      // Auth0と同様に非同期でコールバックを呼ぶ
      setTimeout(() => callback(error), 0);
      return;
    }

    // sendパラメータのバリデーション（Auth0互換）
    if (options.send && options.send !== "code") {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: 'Invalid send parameter. Only "code" is supported.',
      };
      setTimeout(() => callback(error), 0);
      return;
    }

    // connectionによってtypeとdestinationを決定
    let type: "email" | "sms";
    let destination: string;

    if (options.connection === "email") {
      type = "email";
      destination = options.email || "";
      if (!destination) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Email is required for email connection",
        };
        setTimeout(() => callback(error), 0);
        return;
      }

      // メールアドレスの基本的なバリデーション（Auth0互換）
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(destination)) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Invalid email format",
        };
        setTimeout(() => callback(error), 0);
        return;
      }
    } else if (options.connection === "sms") {
      type = "sms";
      destination = options.phoneNumber || "";
      if (!destination) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Phone number is required for sms connection",
        };
        setTimeout(() => callback(error), 0);
        return;
      }

      // 電話番号の基本的なバリデーション（+81形式で始まる日本の番号）
      if (!destination.startsWith("+81")) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Phone number must be in international format (+81...)",
        };
        setTimeout(() => callback(error), 0);
        return;
      }
    } else {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: 'Invalid connection type. Must be "email" or "sms"',
      };
      setTimeout(() => callback(error), 0);
      return;
    }

    // dm-id-server用のリクエストデータを構築
    const passwordlessStartRequest = {
      client_id: this.clientID,
      connection: options.connection, // 'email' or 'sms'
      send: "code", // 固定値
      // connectionに応じて適切なフィールドを設定
      ...(options.connection === "email" ? { email: destination } : { phone_number: destination }),
      authParams: {
        response_type: options.authParams?.responseType || this.responseType || "token",
        redirect_uri: options.authParams?.redirectUri || this.redirectUri,
        scope: options.authParams?.scope || this.scope || "openid profile email",
        state: options.authParams?.state,
      },
    };

    // dm-id-serverの/passwordless/startエンドポイントに送信
    this.httpClient
      .post<AuthPasswordlessStartResponse>("/passwordless/start", passwordlessStartRequest)
      .then((response) => {
        // Auth0互換: 成功時のレスポンス形式を調整
        const successResponse = {
          ...response.data,
          email: type === "email" ? destination : undefined,
          phoneNumber: type === "sms" ? destination : undefined,
        };

        // 成功時はレスポンスデータをコールバックに渡す
        callback(null, successResponse);
      })
      .catch((error: Auth0Error | unknown) => {
        // axiosインターセプターで既にAuth0Error形式に変換されている場合はそのまま使用
        if (this.isAuth0Error(error)) {
          callback(error);
        } else {
          // フォールバック：インターセプターを通らなかった場合の処理
          const auth0Error = this.convertHttpErrorToAuth0Error(error);
          callback(auth0Error);
        }
      });
  }

  /**
   * OTP検証（パスワードレス認証） - OAuth 2.1 Authorization Code Flow + PKCE
   * dm-id-server /authorize エンドポイント対応
   */
  async passwordlessLogin(options: PasswordlessLoginOptions, callback: PasswordlessLoginCallback): Promise<void> {
    // パラメータバリデーション - Auth0と同様のエラーハンドリング
    if (!options.connection) {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: "Connection type is required",
      };
      callback(error);
      return;
    }

    // connectionによってtypeとdestinationを決定
    let type: "email" | "sms";
    let destination: string;

    if (options.connection === "email") {
      type = "email";
      destination = options.email || "";
      if (!destination) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Email is required for email connection",
        };
        callback(error);
        return;
      }

      // メールアドレスの基本的なバリデーション（Auth0互換）
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(destination)) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Invalid email format",
        };
        callback(error);
        return;
      }
    } else if (options.connection === "sms") {
      type = "sms";
      destination = options.phoneNumber || "";
      if (!destination) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Phone number is required for sms connection",
        };
        callback(error);
        return;
      }

      // 電話番号の基本的なバリデーション（+81形式で始まる日本の番号）
      if (!destination.startsWith("+81")) {
        const error: Auth0Error = {
          error: "invalid_request",
          errorDescription: "Phone number must be in international format (+81...)",
        };
        callback(error);
        return;
      }
    } else {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: 'Invalid connection type. Must be "email" or "sms"',
      };
      callback(error);
      return;
    }

    if (!options.verificationCode) {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: "Verification code is required",
      };
      callback(error);
      return;
    }

    // OTPコードの基本的なバリデーション（4桁の数字）
    const otpRegex = /^\d{4}$/;
    if (!otpRegex.test(options.verificationCode)) {
      const error: Auth0Error = {
        error: "invalid_request",
        errorDescription: "Verification code must be 4 digits",
      };
      callback(error);
      return;
    }

    try {
      // PKCE & state 生成
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateOAuthState();

      // PKCE状態をCookieに保存（5分間）
      setPkceState(codeVerifier, state);

      // OAuth 2.1 Authorization Code Flow用のリクエストデータを構築
      const authorizeRequest = {
        response_type: "code",
        client_id: this.clientID,
        redirect_uri: options.redirectUri || this.redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        scope: options.scope || this.scope || "openid profile email",
        state: state,
        credentials: {
          type,
          destination,
          verification_code: options.verificationCode,
        },
      };

      // dm-id-serverの /authorize エンドポイントに送信（POST JSON形式）
      await this.httpClient
        .post("/authorize", authorizeRequest)
        .then((response) => {
          // 200 OK + JSONレスポンスの場合（Auth0方式）
          if (response.status === 200 && response.data) {
            // 成功時: redirect_urlでリダイレクト
            if (response.data.status === "success" && response.data.redirect_uri) {
              try {
                // ブラウザを手動でリダイレクト
                window.location.href = response.data.redirect_uri;
              } catch (redirectError) {
                console.error("[ERROR] window.location.href failed:", redirectError);
                const auth0Error: Auth0Error = {
                  error: "server_error",
                  errorDescription: "Failed to redirect browser",
                  original: redirectError,
                };
                callback(auth0Error);
              }
              return;
            }

            // エラー時: エラーハンドリング
            if (response.data.status === "error") {
              const auth0Error: Auth0Error = {
                error: response.data.error || "access_denied",
                errorDescription: response.data.error_description || "Authentication failed",
                statusCode: response.status,
              };
              callback(auth0Error);
              return;
            }

            // 予期しないレスポンス形式
            const auth0Error: Auth0Error = {
              error: "server_error",
              errorDescription: "Unexpected response format from server",
              statusCode: response.status,
            };
            callback(auth0Error);
            return;
          }

          // その他のステータスコードの場合
          const errorData = response.data || {};
          const auth0Error: Auth0Error = {
            error: errorData.error || "server_error",
            errorDescription: errorData.error_description || `Unexpected HTTP status: ${response.status}`,
            statusCode: response.status,
          };

          callback(auth0Error);
        })
        .catch((error: Auth0Error | unknown) => {
          // PKCE状態をクリア（エラー時）
          clearPkceState();

          // axiosインターセプターで既にAuth0Error形式に変換されている場合はそのまま使用
          if (this.isAuth0Error(error)) {
            callback(error);
          } else {
            // フォールバック：インターセプターを通らなかった場合の処理
            const auth0Error = this.convertHttpErrorToAuth0Error(error);
            callback(auth0Error);
          }
        });
    } catch (error: unknown) {
      // PKCE生成エラー
      const pkceError: Auth0Error = {
        error: "server_error",
        errorDescription: "Failed to generate PKCE parameters",
        original: error,
      };
      callback(pkceError);
    }
  }

  /**
   * セッション確認 - OAuth 2.1標準準拠の効率的な実装
   * 1. Access Token有効性チェック（ローカル）
   * 2. 必要時のみRefresh Token Grant実行
   */
  checkSession(callback: CheckSessionCallback): void {
    // 1. Access Tokenが有効な場合は即座に完了（サーバー呼び出しなし）
    if (this.isAccessTokenValid()) {
      const accessToken = this.tokenStorage?.getAccessToken();
      const auth0Result: Auth0DecodedHash = {
        accessToken: accessToken!,
        // 既存のトークン情報を保持
        refreshToken: this.tokenStorage?.getRefreshToken() || undefined,
        tokenType: "Bearer",
      };
      callback(null, auth0Result);
      return;
    }

    // 2. Access Token期限切れ/未保持時のみRefresh Token Grantを実行
    const refreshToken = this.tokenStorage?.getRefreshToken();

    if (!refreshToken) {
      const error: Auth0Error = {
        error: "login_required",
        errorDescription: "No refresh token available. Login required.",
      };
      callback(error, null);
      return;
    }

    // 3. OAuth 2.1標準: POST /token with refresh_token grant
    const tokenRequest = {
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: this.clientID,
    };

    // dm-id-serverの標準OAuth 2.1エンドポイント /token を使用
    this.httpClient
      .post("/oauth/token", tokenRequest)
      .then((response) => {
        // OAuth 2.1標準レスポンス処理
        const tokenResponse = response.data;

        // OAuth 2.1準拠: Refresh Token Rotationでトークンを更新
        if (this.tokenStorage) {
          this.tokenStorage.saveTokens({
            accessToken: tokenResponse.access_token,
            refreshToken: tokenResponse.refresh_token, // 新しいrefresh_token
            expiresIn: tokenResponse.expires_in || 3600,
            tokenType: tokenResponse.token_type || "Bearer",
            scope: tokenResponse.scope,
          });
        }

        // Auth0DecodedHash形式に変換してコールバック
        const auth0Result: Auth0DecodedHash = {
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          expiresIn: tokenResponse.expires_in,
          tokenType: tokenResponse.token_type || "Bearer",
          scope: tokenResponse.scope,
        };

        callback(null, auth0Result);
      })
      .catch((error: Auth0Error | unknown) => {
        // refresh_token無効 → トークンクリア & login_required
        if (this.tokenStorage) {
          this.tokenStorage.clearTokens();
        }

        // エラー形式の統一処理
        const auth0Error = this.isAuth0Error(error) ? error : this.convertHttpErrorToAuth0Error(error);

        const loginRequiredError: Auth0Error = {
          error: "login_required",
          errorDescription: "Session expired. Login required.",
          statusCode: auth0Error.statusCode,
        };
        callback(loginRequiredError, null);
      });
  }

  /**
   * ログアウト処理
   * auth0.webAuth.logout()と完全互換
   *
   * Auth0互換性:
   * - エラー発生時でも必ずトークンクリアとリダイレクトを実行
   * - returnToパラメータの完全対応
   * - OAuth 2.1準拠のトークン管理統合
   */
  logout(options: LogoutOptions): void {
    // パラメータバリデーション（Auth0互換）
    if (!options || typeof options !== "object") {
      console.warn("logout: options parameter is required");
      return;
    }

    // OAuth 2.1準拠: TokenStorageからaccessTokenを取得
    const accessToken = this.tokenStorage?.getAccessToken();

    // アクセストークンが存在しない場合の処理（Auth0互換）
    if (!accessToken) {
      // トークンクリアとリダイレクトは実行
      if (this.tokenStorage) {
        this.tokenStorage.clearTokens();
      }
      if (options.returnTo) {
        window.location.href = options.returnTo;
      }
      return;
    }

    // dm-id-server用のリクエストデータを構築
    const logoutRequest = {
      access_token: accessToken,
      refresh_token: this.tokenStorage?.getRefreshToken() || undefined,
      client_id: this.clientID,
      returnTo: options.returnTo,
    };

    //標準のContent-Typeヘッダー
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // dm-id-serverの/logoutエンドポイントに送信
    this.httpClient
      .post<AuthLogoutResponse>("/logout", logoutRequest, { headers })
      .then((response) => {
        // OAuth 2.1準拠: TokenStorageでトークンをクリア
        if (this.tokenStorage) {
          this.tokenStorage.clearTokens();
        }

        // ログアウト成功をログ出力
        console.info("Logout successful", {
          success: response.data?.success,
          returnTo: options.returnTo,
        });

        // Auth0と同様にreturnToにリダイレクト
        if (options.returnTo) {
          window.location.href = options.returnTo;
        }
      })
      .catch((error: Auth0Error) => {
        // エラーが発生してもトークン情報はクリアする（Auth0互換動作）
        if (this.tokenStorage) {
          this.tokenStorage.clearTokens();
        }

        // エラーの詳細をログ出力（Auth0互換：警告レベル）
        console.warn("Logout API error (continuing with redirect):", {
          error: error.error || "unknown_error",
          description: error.errorDescription || "Logout API request failed",
          statusCode: error.statusCode,
          returnTo: options.returnTo,
        });

        // Auth0ではlogoutはエラーが発生してもリダイレクトを実行する
        // これは重要なAuth0互換動作：ログアウトは必ず成功扱い
        if (options.returnTo) {
          window.location.href = options.returnTo;
        }
      });
  }

  /**
   * Authorization Code処理（フラグメント解析 + トークン交換）
   * OAuth 2.1 Authorization Code Flow + PKCE対応
   */
  parseHash(options: ParseHashOptions, callback: ParseHashCallback): void {
    try {
      // URLフラグメントからAuthorization Codeを取得
      const hash = options.hash || window.location.hash;

      if (!hash || hash.length <= 1) {
        // フラグメントが存在しない場合はnullを返す（エラーではない）
        callback(null, null);
        return;
      }

      // エラーレスポンスをチェック（Auth0互換）
      if (hash.includes("error=") || hash.includes("error_description=")) {
        const errorParams = new URLSearchParams(hash.substring(1));
        const errorCode = errorParams.get("error");
        const errorDescription = errorParams.get("error_description");

        // PKCE状態クリア（エラー時）
        clearPkceState();

        const auth0Error: Auth0Error = {
          error: errorCode || "unknown_error",
          errorDescription: errorDescription || "An error occurred during authentication",
          state: errorParams.get("state") || undefined,
        };

        callback(auth0Error, null);
        return;
      }

      // '#'を除去してパラメータを解析
      const hashParams = new URLSearchParams(hash.substring(1));

      // Authorization Codeを取得
      const authorizationCode = hashParams.get("code");
      const receivedState = hashParams.get("state");

      if (!authorizationCode) {
        // codeが存在しない場合は認証結果ではない
        callback(null, null);
        return;
      }

      // PKCE状態を取得・検証
      const pkceState = getPkceState();
      if (!pkceState) {
        const pkceError: Auth0Error = {
          error: "invalid_request",
          errorDescription: "PKCE state not found or expired",
        };
        callback(pkceError, null);
        return;
      }

      // state検証（CSRF攻撃防止）
      if (receivedState !== pkceState.state) {
        clearPkceState();
        const stateError: Auth0Error = {
          error: "invalid_request",
          errorDescription: "State parameter mismatch",
        };
        callback(stateError, null);
        return;
      }

      // Authorization Code → Access Token交換
      this.exchangeCodeForTokens(authorizationCode, pkceState.codeVerifier, callback);
    } catch (error: unknown) {
      // 解析エラーが発生した場合
      clearPkceState();
      const parseError: Auth0Error = {
        error: "invalid_request",
        errorDescription: error instanceof Error ? error.message : "Failed to parse authorization code",
        original: error,
      };
      callback(parseError, null);
    }
  }

  /**
   * Authorization Code → Access Token交換
   * OAuth 2.1 Token Exchange + PKCE検証
   */
  private exchangeCodeForTokens(authorizationCode: string, codeVerifier: string, callback: ParseHashCallback): void {
    // OAuth 2.1 Token Requestを構築
    const tokenRequest = {
      grant_type: "authorization_code",
      client_id: this.clientID,
      code: authorizationCode,
      redirect_uri: this.redirectUri,
      code_verifier: codeVerifier,
    };

    // dm-id-serverの /oauth/token エンドポイントに送信
    this.httpClient
      .post("/oauth/token", tokenRequest)
      .then((response) => {
        const tokenResponse = response.data;

        // OAuth 2.1準拠: トークンをTokenStorageに保存
        if (this.tokenStorage && tokenResponse.access_token) {
          this.tokenStorage.saveTokens({
            accessToken: tokenResponse.access_token,
            refreshToken: tokenResponse.refresh_token,
            expiresIn: tokenResponse.expires_in || 3600,
            tokenType: tokenResponse.token_type || "Bearer",
            scope: tokenResponse.scope,
          });
        }

        // Auth0DecodedHash形式でレスポンスを構築
        const authResult: Auth0DecodedHash = {
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token || undefined,
          expiresIn: tokenResponse.expires_in,
          tokenType: tokenResponse.token_type || "Bearer",
          scope: tokenResponse.scope || undefined,
          idToken: tokenResponse.id_token || undefined,
        };

        // PKCE状態をクリア（成功時）
        clearPkceState();

        // 成功時はauthResultをコールバックに渡す
        callback(null, authResult);
      })
      .catch((error: Auth0Error | unknown) => {
        // PKCE状態をクリア（エラー時）
        clearPkceState();

        // axiosインターセプターで既にAuth0Error形式に変換されている場合はそのまま使用
        if (this.isAuth0Error(error)) {
          callback(error, null);
        } else {
          // フォールバック：インターセプターを通らなかった場合の処理
          const auth0Error = this.convertHttpErrorToAuth0Error(error);
          callback(auth0Error, null);
        }
      });
  }

  /**
   * JWTトークンの有効期限をチェック
   * ローカルでの検証により、不要なサーバー呼び出しを削減
   */
  private isTokenExpired(token: string): boolean {
    try {
      // JWTの payload部分をデコード (header.payload.signature)
      const payloadBase64 = token.split(".")[1];
      if (!payloadBase64) return true;

      // Base64デコード (URLSafeな文字を標準Base64に変換)
      const payloadJson = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));
      const payload = JSON.parse(payloadJson);

      // expクレーム確認
      if (!payload.exp) return true;

      // 現在時刻と比較 (expは秒単位、Date.now()はミリ秒単位)
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = payload.exp;

      // 30秒のバッファを設けて期限切れを判定
      return currentTime >= expirationTime - 30;
    } catch (error) {
      // デコードエラーの場合は期限切れとみなす
      return true;
    }
  }

  /**
   * Access Tokenの有効性をチェック（存在 + 期限内）
   */
  private isAccessTokenValid(): boolean {
    const accessToken = this.tokenStorage?.getAccessToken();
    if (!accessToken) return false;

    return !this.isTokenExpired(accessToken);
  }

  /**
   * Auth0Errorの型ガード
   */
  private isAuth0Error(error: unknown): error is Auth0Error {
    return typeof error === "object" && error !== null && "error" in error && "errorDescription" in error;
  }

  /**
   * HTTPエラーをAuth0Error形式に変換
   */
  private convertHttpErrorToAuth0Error(error: unknown): Auth0Error {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const axiosError = error as any;

    if (axiosError?.response?.data) {
      // 新認証基盤からのエラーレスポンス
      const errorData = axiosError.response.data;
      return {
        error: errorData.error || this.mapHttpStatusToAuth0Error(axiosError.response.status),
        errorDescription:
          errorData.error_description || errorData.message || this.getDefaultErrorMessage(axiosError.response.status),
        statusCode: axiosError.response.status,
        original: errorData,
      };
    } else if (axiosError?.code === "ECONNABORTED" || axiosError?.message?.includes("timeout")) {
      // タイムアウトエラー
      return {
        error: "timeout",
        errorDescription: "Request timeout. Please try again.",
        statusCode: 408,
        original: error,
      };
    } else if (axiosError?.code === "NETWORK_ERROR" || !axiosError?.response) {
      // ネットワークエラー（エンドポイントが存在しない可能性）
      const url = axiosError?.config?.url || "unknown";
      return {
        error: "network_error",
        errorDescription: `Network connection failed. The endpoint ${url} may not be available. This could be due to a server-side module error (OAuth2Errors not found). Please check if the server is properly deployed and all required modules are available.`,
        statusCode: 0,
        original: error,
      };
    } else {
      // その他のエラー
      return {
        error: "server_error",
        errorDescription: axiosError?.message || "Unknown server error occurred",
        statusCode: axiosError?.response?.status || 500,
        original: error,
      };
    }
  }

  /**
   * HTTPステータスコードをAuth0エラーコードにマップ
   */
  private mapHttpStatusToAuth0Error(statusCode: number): string {
    switch (statusCode) {
      case 400:
        return "invalid_request";
      case 401:
        return "unauthorized";
      case 403:
        return "access_denied";
      case 404:
        return "not_found";
      case 429:
        return "too_many_requests";
      case 500:
        return "server_error";
      case 502:
      case 503:
      case 504:
        return "service_unavailable";
      default:
        return "unknown_error";
    }
  }

  /**
   * HTTPステータスコードのデフォルトエラーメッセージを取得
   */
  private getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
      case 400:
        return "Bad request. Please check your input parameters.";
      case 401:
        return "Invalid credentials. Please check your username and password.";
      case 403:
        return "Access denied. You do not have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 429:
        return "Too many requests. Please try again later.";
      case 500:
        return "Internal server error. Please try again later.";
      case 502:
      case 503:
      case 504:
        return "Service temporarily unavailable. Please try again later.";
      default:
        return "An error occurred while processing your request.";
    }
  }
}
